import { SEOLEIMRUN_CDN_RECEIPT } from 'src/app/(seoleimrun)/_constants/cdn';
import { SEOLEIMRUN_MY_LINKS } from 'src/app/(seoleimrun)/seoleimrun/_components/constants';

import {
  SEOLEIMRUN_RECEIPT_ERROR_STATUS,
  type SeoleimrunReceiptErrorStatus,
} from '@/store/server/common/seoleimrun/seoleimrun.dto';

// bg.webp(declared 480×894) — 카메라/앨범 버튼 제외 전부(타이틀·영수증 그래픽·유의사항) 포함. 상단 60px는 GNB가 덮음.
export const RECEIPT_BG = { src: `${SEOLEIMRUN_CDN_RECEIPT}/bg.webp`, width: 480, height: 894 } as const;

// bg_result.webp(declared 480×820) — 결과 화면 배경(파란 bg + "영수증 인증 이벤트" 타이틀 + 프린터 + 빈 영수증 종이). 상단 60px는 GNB가 덮음.
// 빈 영수증 종이 영역 위에 결과 콘텐츠(아이콘·문구·촬영 썸네일)를, 하단 파란 영역에 CTA 버튼을 오버레이한다.
export const RECEIPT_RESULT_BG = { src: `${SEOLEIMRUN_CDN_RECEIPT}/bg_result.webp`, width: 480, height: 820 } as const;

// 카메라 3초 안내 아이콘(파란 영수증 + 체크 + 스캔 코너 프레임) — Figma 758-22443. 100×100.
export const RECEIPT_OCR_GUIDE_ICON = `${SEOLEIMRUN_CDN_RECEIPT}/ocr_guide.webp`;
// 인식중 보라 스캔 밴드 — Figma 758-22367. 화면 폭 풀블리드로 위→아래 이동(seoleimrun-scan).
export const RECEIPT_SCAN_BAND = `${SEOLEIMRUN_CDN_RECEIPT}/scan.webp`;

// 카메라 캡처 영역 비율 — 표시영역 대비 가로 92% / 세로 78%. ReceiptCamera 가 이 영역만 잘라 캡처한다.
// (인식중 화면 ReceiptScanning 은 풀스크린 object-contain 이라 이 비율을 쓰지 않는다.)
export const SCAN_WIDTH_RATIO = 0.92;
export const SCAN_HEIGHT_RATIO = 0.78;

// 표시 전용 미리보기 — 인식중/결과 화면 <img> 에 풀해상도 원본 대신 쓰는 축소본.
// 인식중 화면은 풀스크린 object-contain 이라 표시 장축이 컨테이너 높이(h-dvh, 모바일 ~700–900 CSS px)까지 커진다.
// DPR 2–3 기준 기기 픽셀 장축 ~2000–2700 을 커버하도록 장축 2000px(≈4MP) — 디코딩 부담은 작아 스캔 시작 멈춤 없음.
// 업로드(OCR)는 원본을 그대로 보내므로 이 축소는 인식 정확도에 영향이 없다.
export const PREVIEW_MAX_EDGE = 2000;
export const PREVIEW_QUALITY = 0.75;

// 하단 고정(fixed) 2버튼 바 높이(px). 레이아웃이 영수증 페이지에서만 이만큼 paddingBottom 으로 예약 → 바가 Footer/콘텐츠를 가리지 않음.
// 계산: py-5(20×2) + Button large(py-14×2 + lineheight20) = 40 + 48 = 88.
export const RECEIPT_BOTTOM_BAR_HEIGHT = 88;

// 업로드 검증 — 디자인 desc2 기준. JPG·PNG, 10MB 이하.
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];

interface ReceiptErrorModalContent {
  /** 모달 타이틀 — 줄 단위 배열 */
  title: string[];
  /** 모달 설명 — 줄 단위 배열 */
  description: string[];
  /**
   * 지정 시 alert 대신 confirm 모달로 노출한다.
   * 왼쪽 secondary 버튼에 이 라벨/이동 경로가 바인딩되고, 오른쪽 primary '확인'은 alert 와 동일하게 닫기만 한다.
   */
  link?: { text: string[]; href: string };
}

// 영수증 인증 실패 모달 문구 — status code별 분기. Figma 1204-20830(추가 모달).
// 아이콘은 글로벌 danger(회색 원 + brand-01 느낌표)로 공통, 타이틀/설명만 분기한다.
// 단 `link` 가 있는 코드(ALREADY_REGISTERED)만 alert 대신 confirm 으로 노출해 이동 버튼을 하나 더 준다.
// Record<union> 으로 타이핑해 코드 추가/삭제 시 TS 가 누락을 강제(exhaustive).
export const RECEIPT_ERROR_MODAL: Record<SeoleimrunReceiptErrorStatus, ReceiptErrorModalContent> = {
  [SEOLEIMRUN_RECEIPT_ERROR_STATUS.NOT_RECEIPT]: {
    title: ['영수증을 인식할 수 없어요'],
    description: ['구매 영수증을 촬영해서 다시 인증해 주세요.'],
  },
  [SEOLEIMRUN_RECEIPT_ERROR_STATUS.INVALID_GEMINI_FORMAT]: {
    title: ['영수증을 읽지 못했어요'],
    description: ['글씨가 잘 보이게 다시 촬영해 주세요.'],
  },
  [SEOLEIMRUN_RECEIPT_ERROR_STATUS.OCR_BLACKLISTED]: {
    title: ['참여가 제한된 영수증이에요'],
    description: ['자세한 내용은 고객센터로 문의해 주세요.'],
  },
  [SEOLEIMRUN_RECEIPT_ERROR_STATUS.ALREADY_REGISTERED]: {
    title: ['이미 등록된 영수증이에요'],
    description: ['다른 영수증으로 다시 참여해 주세요.'],
    // 이미 등록해 둔 내역이 있다는 뜻이므로 MY 영수증 내역으로 바로 갈 수 있게 한다.
    link: { text: ['내역 보러가기'], href: SEOLEIMRUN_MY_LINKS.receipt },
  },
  [SEOLEIMRUN_RECEIPT_ERROR_STATUS.NOT_PURCHASED]: {
    title: ['설레임 구매 내역이 없어요'],
    description: ['설레임이 포함된 영수증으로 참여해 주세요.'],
  },
};

// status null(네트워크 오류·응답 없음) / 5종 외 미매핑 코드 대비 기본 문구.
export const RECEIPT_ERROR_MODAL_FALLBACK: ReceiptErrorModalContent = {
  title: ['영수증 인증에 실패했어요'],
  description: ['잠시 후 다시 시도해 주세요.'],
};
